import React, { useEffect, useState } from 'react';
import { APP_SHORT_NAME } from './constants/branding';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout/Layout';
import Login from './pages/Auth/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import Users from './pages/Users/Users';
import AddDonations from './pages/Donations/AddDonation';
import ManageDonations from './pages/Donations/ManageDonations';
import DonationTypes from './pages/Donations/DonationTypes';
import AddDonors from './pages/Donors/AddDonors';
import AddGuestDonors from './pages/Donors/AddGuestDonors';
import ManageDonors from './pages/Donors/ManageDonors';
import ManageGuestDonors from './pages/Donors/ManageGuestDonors';
import AdvancedReports from './pages/Reports/AdvancedReports';
import Settings from './pages/Settings/Settings';
import Regions from './pages/Regions/Regions';
import ManageRegions from './pages/Regions/ManageRegions';
import Offices from './pages/Offices/Offices';
import ManageOffices from './pages/Offices/ManageOffices';
import Controllers from './pages/Controllers/Controllers';
import ManageControllers from './pages/Controllers/ManageControllers';
import AddExpenses from './pages/Expenses/AddExpense';
import ManageExpenses from './pages/Expenses/ManageExpenses';
import ExpenseTypes from './pages/Expenses/ExpenseTypes';
import AddIncome from './pages/Income/AddIncome';
import ManageIncomes from './pages/Income/ManageIncomes';
import IncomeDepartments from './pages/Income/IncomeDepartments';
import IncomeTypes from './pages/Income/IncomeTypes';
import Accounts from './pages/Accounts/Accounts';
import AuditLogs from './pages/AuditLogs/AuditLogs';
import ScheduledReports from './pages/Automation/ScheduledReports';
import AutomatedBackups from './pages/Automation/AutomatedBackups';
import { useAuthStore } from './stores/authStore';
import { useFeatureAccessStore } from './stores/featureAccessStore';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { UpdateNotification } from './components/UpdateNotification';


function App() {
  const { isAuthenticated, refreshSession, isLoading, user, checkTokenExpiration } = useAuthStore();
  const [isInitializing, setIsInitializing] = useState(true);
  const { hasFeature, fetchCurrentUserFeatures } = useFeatureAccessStore();
  const [featuresInit, setFeaturesInit] = useState(false);

  const AccessDenied: React.FC = () => (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">Access Denied</h2>
        <p className="text-gray-600 dark:text-gray-400">You do not have permission to access this page.</p>
      </div>
    </div>
  );

  const RequireFeature: React.FC<{ feature?: string } & { children: React.ReactElement }> = ({ feature, children }) => {
    if (!feature) return children;
    if (user?.role === 'SuperAdmin') return children;
    if (!featuresInit) return <div className="min-h-[200px] flex items-center justify-center text-gray-500">Loading…</div>;
    if (hasFeature(feature as any)) return children;
    return <AccessDenied />;
  };

  useEffect(() => {
    const initializeApp = async () => {
      try {
        await refreshSession();
      } catch (error) {
        console.error('Failed to refresh session:', error);
      } finally {
        setIsInitializing(false);
      }
    };
    
    initializeApp();
  }, [refreshSession]);

  // Set up token expiration check every minute
  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(() => {
        checkTokenExpiration();
      }, 60000); // Check every minute

      return () => clearInterval(interval);
    }
  }, [isAuthenticated, checkTokenExpiration]);

  useEffect(() => {
    (async () => {
      try {
        if (isAuthenticated) {
          await fetchCurrentUserFeatures();
        }
      } catch {}
      setFeaturesInit(true);
    })();
  }, [isAuthenticated, fetchCurrentUserFeatures]);

  // Show loading while initializing
  if (isInitializing || isLoading || !featuresInit) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="text-center">
          {/* Beautiful Loading Spinner */}
          <div className="loading-spinner-beautiful mx-auto mb-6"></div>
          
          {/* Loading Text with Animation */}
          <div className="loading-dots mb-4">
            <div></div>
            <div></div>
            <div></div>
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {APP_SHORT_NAME}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 text-lg">
            Loading your dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <ToastContainer
        position="top-right"
        autoClose={2500}
        hideProgressBar
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss={false}
        draggable={false}
        pauseOnHover
        theme="light"
        toastClassName="shadow-lg rounded-md border"
        toastStyle={{
          backgroundColor: 'var(--toast-bg)',
          color: 'var(--toast-color)',
          border: '1px solid var(--toast-border)',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }}
      />
      <Routes>
        <Route path="/login" element={
          isAuthenticated ? <Navigate to="/" replace /> : <Login />
        } />
        <Route path="/" element={
          isAuthenticated ? <Layout /> : <Navigate to="/login" replace />
        }>
          <Route index element={<Dashboard />} />
          <Route path="settings" element={<Settings />} />
          
          {/* Donation Routes */}
          <Route path="add-donation" element={<RequireFeature feature={'addDonation' as any}><AddDonations /></RequireFeature>} />
          <Route path="donations" element={<RequireFeature feature={'manageDonations' as any}><ManageDonations /></RequireFeature>} />
          <Route path="add-donors" element={<RequireFeature feature={'manageDonors' as any}><AddDonors /></RequireFeature>} />
          <Route path="donors" element={<RequireFeature feature={'manageDonors' as any}><ManageDonors /></RequireFeature>} />
          <Route path="add-guest-donors" element={<RequireFeature feature={'manageDonors' as any}><AddGuestDonors /></RequireFeature>} />
          <Route path="guest-donors" element={<RequireFeature feature={'manageDonors' as any}><ManageGuestDonors /></RequireFeature>} />
          
          {/* Expense Routes */}
          <Route path="add-expense" element={<RequireFeature feature={'addExpense' as any}><AddExpenses /></RequireFeature>} />
          <Route path="expenses" element={<RequireFeature feature={'manageExpenses' as any}><ManageExpenses /></RequireFeature>} />
          <Route path="expense-types" element={<RequireFeature feature={'manageExpenseTypes' as any}><ExpenseTypes /></RequireFeature>} />

          {/* Income Routes */}
          <Route path="add-income" element={<RequireFeature feature={'addIncome' as any}><AddIncome /></RequireFeature>} />
          <Route path="incomes" element={<RequireFeature feature={'manageIncomes' as any}><ManageIncomes /></RequireFeature>} />
          <Route path="income-departments" element={<RequireFeature feature={'manageIncomeDepartments' as any}><IncomeDepartments /></RequireFeature>} />
          <Route path="income-types" element={<RequireFeature feature={'manageIncomeTypes' as any}><IncomeTypes /></RequireFeature>} />
          
          {/* Donation Types */}
          <Route path="donation-types" element={<RequireFeature feature={'manageDonationTypes' as any}><DonationTypes /></RequireFeature>} />
          
          
          {/* Accounts */}
          <Route path="accounts" element={<RequireFeature feature={'viewAccounts' as any}><Accounts /></RequireFeature>} />
          
          {/* Organization Routes */}
          <Route path="view-regions" element={<RequireFeature feature={'viewRegions' as any}><Regions /></RequireFeature>} />
          <Route path="regions" element={<RequireFeature feature={'manageRegions' as any}><ManageRegions /></RequireFeature>} />
          <Route path="view-offices" element={<RequireFeature feature={'viewOffices' as any}><Offices /></RequireFeature>} />
          <Route path="offices" element={<RequireFeature feature={'manageOffices' as any}><ManageOffices /></RequireFeature>} />
          <Route path="view-controllers" element={<RequireFeature feature={'viewControllers' as any}><Controllers /></RequireFeature>} />
          <Route path="controllers" element={<RequireFeature feature={'manageControllers' as any}><ManageControllers /></RequireFeature>} />
          
          {/* Reports and Users */}
          <Route path="reports" element={<RequireFeature feature={'accessReports' as any}><AdvancedReports /></RequireFeature>} />
          <Route path="automation-backups" element={<RequireFeature feature={'manageBackupAutomation' as any}><AutomatedBackups /></RequireFeature>} />
          <Route path="automation-reports" element={<RequireFeature feature={'manageScheduledReports' as any}><ScheduledReports /></RequireFeature>} />
          <Route path="users" element={<RequireFeature feature={'manageUsers' as any}><Users /></RequireFeature>} />
          
          {/* Audit Logs - SuperAdmin Only */}
          <Route path="audit-logs" element={
            user?.role === 'SuperAdmin' ? <AuditLogs /> : <AccessDenied />
          } />
        </Route>
      </Routes>
      {/* Auto-update notification toast — renders when update is available/downloading/downloaded */}
      <UpdateNotification />
    </div>
  );
}

export default App; 